import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getCategoryTree } from "@/lib/api-client";
import { getWhatsAppContact } from "@/lib/contact";

/**
 * Pied de page global (VVS_MODELE_INTERFACE.md §1.6 réassurance, §1.8 pied de
 * page, §1.9 barre légale). Porté depuis apps/web/components/layout/Footer.tsx
 * (Server Component) — devenu un composant client (TanStack Query pour la
 * colonne "Acheter" et le contact WhatsApp) ; `next/image` → `<img>`,
 * `next/link` → `react-router-dom` `Link`.
 *
 * Pas de newsletter (§1.7) : elle écrirait dans
 * `customer.accepts_marketing_email/_sms`, un canal de consentement non
 * exposé par `@/lib/api-client` — construire un formulaire qui a l'air de
 * fonctionner mais n'écrit nulle part serait trompeur, donc omis plutôt que
 * simulé (même choix que l'original).
 */

interface FooterLink {
  label: string;
  href: string;
}

interface FooterColumn {
  title: string;
  links: FooterLink[];
}

// Colonnes "Aide" et "Mon compte" : texte statique assumé — pas de `page` /
// `menu` CMS à consommer pour ce lot (VVS_MODELE_INTERFACE.md §1.8).
const HELP_COLUMN: FooterColumn = {
  title: "Aide",
  links: [
    { label: "Livraison", href: "/livraison" },
    { label: "Retours", href: "/retours" },
    { label: "Paiement", href: "/paiement" },
    { label: "FAQ", href: "/faq" },
    { label: "Nous contacter", href: "/contact" },
  ],
};

const ACCOUNT_COLUMN: FooterColumn = {
  title: "Mon compte",
  links: [
    { label: "Mon compte", href: "/compte" },
    { label: "Mes commandes", href: "/compte/commandes" },
    { label: "Mon panier", href: "/panier" },
    { label: "Mes favoris", href: "/compte/favoris" },
  ],
};

const REASSURANCE_ITEMS: Array<{ title: string; subtitle: string; href: string }> = [
  { title: "Produits authentiques", subtitle: "Vendeurs vérifiés", href: "/a-propos" },
  { title: "Livraison rapide", subtitle: "24 h à Abidjan", href: "/livraison" },
  { title: "Paiement au choix", subtitle: "COD ou mobile money", href: "/paiement" },
  { title: "Retours simples", subtitle: "Sous conditions", href: "/retours" },
  { title: "Aide", subtitle: "Téléphone, WhatsApp", href: "/contact" },
];

async function loadShopColumn(): Promise<FooterColumn> {
  // Colonne "Acheter" (§1.8) : rayons réels quand l'API répond, repli
  // statique minimal sinon — jamais de rayons inventés.
  try {
    const tree = await getCategoryTree();
    const categoryLinks = tree
      .filter((category) => category.isVisible)
      .map((category) => ({ label: category.name, href: `/catalogue/${category.slug}` }));

    return {
      title: "Acheter",
      links: [...categoryLinks, { label: "Marques", href: "/marques" }, { label: "Nouveautés", href: "/catalogue?sort=newest" }],
    };
  } catch {
    return {
      title: "Acheter",
      links: [
        { label: "Toutes les catégories", href: "/catalogue" },
        { label: "Marques", href: "/marques" },
      ],
    };
  }
}

export function SiteFooter() {
  const year = new Date().getFullYear();

  const { data: shopColumn } = useQuery({ queryKey: ["catalog", "footer-shop-column"], queryFn: loadShopColumn });
  const { data: whatsapp } = useQuery({ queryKey: ["settings", "whatsapp-contact"], queryFn: getWhatsAppContact });

  const columns: FooterColumn[] = [
    {
      title: "La boutique",
      links: [
        { label: "Qui sommes-nous", href: "/a-propos" },
        { label: "Nous contacter", href: "/contact" },
      ],
    },
    shopColumn ?? { title: "Acheter", links: [{ label: "Toutes les catégories", href: "/catalogue" }] },
    HELP_COLUMN,
    ACCOUNT_COLUMN,
  ];

  return (
    <>
      {/* Bloc de réassurance (§1.6) — cinq promesses statiques, pleine largeur. */}
      <section aria-label="Nos engagements" className="border-t border-line bg-cream2/60">
        <ul className="container-vvs grid grid-cols-2 gap-4 py-8 text-center sm:grid-cols-3 lg:grid-cols-5">
          {REASSURANCE_ITEMS.map((item) => (
            <li key={item.title}>
              <Link to={item.href} className="block">
                <p className="font-display text-sm font-semibold text-navy">{item.title}</p>
                <p className="mt-1 font-body text-xs text-muted">{item.subtitle}</p>
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <footer className="border-t border-line bg-navy text-cream">
        <div className="container-vvs flex flex-col gap-10 py-10 lg:flex-row lg:justify-between lg:gap-16">
          {/* Bloc marque — largeur fixe, jamais dans la grille des colonnes de liens. */}
          <div className="max-w-xs shrink-0">
            <img src="/logo-footer.png" alt="VVS — Vendez Vouse Store" width={160} height={115} className="h-14 w-auto" />
            <p className="mt-4 font-body text-sm text-cream2">Vendez-Vous eShop — le marketplace généraliste ivoirien.</p>
            {/* Réseaux sociaux : icônes + liens statiques assumés — pas de
                `setting.social_links` à consommer pour ce lot. */}
            <ul className="mt-4 flex gap-2.5">
              <li>
                <a
                  href="#"
                  aria-label="Facebook VVS"
                  className="flex h-9 w-9 items-center justify-center rounded-sm bg-cream/10 text-goldl transition-colors hover:bg-cream/20"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                    <path d="M14 8h3V4h-3a4 4 0 0 0-4 4v3H7v4h3v6h4v-6h3l1-4h-4V9a1 1 0 0 1 1-1z" />
                  </svg>
                </a>
              </li>
              <li>
                <a
                  href="#"
                  aria-label="Instagram VVS"
                  className="flex h-9 w-9 items-center justify-center rounded-sm bg-cream/10 text-goldl transition-colors hover:bg-cream/20"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                    <rect x="4" y="4" width="16" height="16" rx="4" />
                    <circle cx="12" cy="12" r="3.6" />
                  </svg>
                </a>
              </li>
              <li>
                <a
                  href="#"
                  aria-label="TikTok VVS"
                  className="flex h-9 w-9 items-center justify-center rounded-sm bg-cream/10 text-goldl transition-colors hover:bg-cream/20"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                    <path d="M14 4v10.5a3.5 3.5 0 1 1-3-3.46" />
                    <path d="M14 7.5A4.5 4.5 0 0 0 18.5 10" />
                  </svg>
                </a>
              </li>
              <li>
                <a
                  href="#"
                  aria-label="WhatsApp VVS"
                  className="flex h-9 w-9 items-center justify-center rounded-sm bg-cream/10 text-goldl transition-colors hover:bg-cream/20"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                    <path d="M4 20l1.3-3.6A7.5 7.5 0 1 1 8 19z" />
                  </svg>
                </a>
              </li>
            </ul>
          </div>

          <div className="grid grid-cols-2 gap-x-8 gap-y-8 sm:grid-cols-4 lg:gap-x-12">
            {columns.map((column) => (
              <div key={column.title}>
                <h2 className="font-display text-sm font-semibold uppercase tracking-wide text-goldl">{column.title}</h2>
                <ul className="mt-3 space-y-2 text-sm text-cream2">
                  {column.links.map((link) => (
                    <li key={link.href}>
                      <Link to={link.href} className="hover:text-gold">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* "Besoin d'aide ?" — numéro encore en placeholder, voir @/lib/contact. */}
        <div className="border-t border-navy3">
          <div className="container-vvs flex flex-col gap-2 py-6">
            <span aria-hidden="true" className="h-1 w-10 rounded-sm bg-gold" />
            <h2 className="font-display text-lg font-semibold text-cream">Besoin d&apos;aide&nbsp;?</h2>
            <a href={whatsapp?.link ?? "#"} target="_blank" rel="noopener noreferrer" className="font-body text-sm text-cream2 hover:text-gold">
              Contactez-nous sur WhatsApp au {whatsapp?.display ?? "[numéro indisponible]"}
            </a>
          </div>
        </div>

        <div className="border-t border-navy3">
          <div className="container-vvs flex flex-col items-center gap-2 py-4 text-xs text-cream2 sm:flex-row sm:justify-between">
            <p>© VVS {year}. Tous droits réservés.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/cgv" className="hover:text-gold">
                CGV
              </Link>
              <Link to="/cgu" className="hover:text-gold">
                CGU
              </Link>
              <Link to="/mentions-legales" className="hover:text-gold">
                Mentions légales
              </Link>
              <Link to="/confidentialite" className="hover:text-gold">
                Confidentialité
              </Link>
              <Link to="/politique-cookies" className="hover:text-gold">
                Cookies
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}
